import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Chip,
  Avatar,
  Fab,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  Checkbox,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Label as LabelIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { ColorPicker } from '../components/ColorPicker/ColorPicker';
import { labelsAPI } from '../services/api';
import { PermissionGuard } from '../components/Permissions/PermissionGuard'; // 14/11/2025 - User Access Management Module Phase 5.2.3

interface Label {
  id: number;
  title: string;
  description: string | null;
  color: string;
  show_on_sidebar: boolean;
  conversation_count: number;
}

interface LabelFormData {
  title: string;
  description: string;
  color: string;
  show_on_sidebar: boolean;
}

const LabelsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [labels, setLabels] = useState<Label[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLabel, setEditingLabel] = useState<Label | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLabel, setSelectedLabel] = useState<Label | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSidebar, setFilterSidebar] = useState<boolean | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [labelToDelete, setLabelToDelete] = useState<Label | null>(null);

  const [formData, setFormData] = useState<LabelFormData>({
    title: '',
    description: '',
    color: '#1f93ff',
    show_on_sidebar: false
  });

  useEffect(() => {
    fetchLabels();
  }, []);

  const fetchLabels = async () => {
    try {
      setLoading(true);
      const data = await labelsAPI.getLabels();
      setLabels(data);
    } catch (error: any) {
      console.error('Error fetching labels:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to fetch labels';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingLabel) {
        await labelsAPI.updateLabel(editingLabel.id, formData);
        setSnackbar({ 
          open: true, 
          message: 'Label updated successfully', 
          severity: 'success' 
        });
      } else {
        await labelsAPI.createLabel(formData);
        setSnackbar({ 
          open: true, 
          message: 'Label created successfully', 
          severity: 'success' 
        });
      }
      fetchLabels();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Error saving label:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save label';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleDelete = async (label: Label) => {
    try {
      await labelsAPI.deleteLabel(label.id);
      setSnackbar({ open: true, message: 'Label deleted successfully', severity: 'success' });
      fetchLabels();
    } catch (error: any) {
      console.error('Error deleting label:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete label';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setDeleteConfirmOpen(false);
      setLabelToDelete(null);
    }
  };

  const handleOpenDialog = (label?: Label) => {
    if (label) {
      setEditingLabel(label);
      setFormData({
        title: label.title,
        description: label.description || '',
        color: label.color,
        show_on_sidebar: label.show_on_sidebar
      });
    } else {
      setEditingLabel(null);
      setFormData({
        title: '',
        description: '',
        color: '#1f93ff',
        show_on_sidebar: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLabel(null);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, label: Label) => {
    setAnchorEl(event.currentTarget);
    setSelectedLabel(label);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLabel(null);
  };

  const filteredLabels = labels.filter(label => {
    const matchesSearch = label.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (label.description && label.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterSidebar === null || label.show_on_sidebar === filterSidebar;
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: labels.length,
    sidebar: labels.filter(l => l.show_on_sidebar).length,
    totalConversations: labels.reduce((sum, l) => sum + l.conversation_count, 0)
  };

  const renderLabelCard = (label: Label) => (
    <Card key={label.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              sx={{ 
                bgcolor: label.color, 
                width: 32, 
                height: 32,
                fontSize: '0.875rem'
              }}
            >
              <LabelIcon fontSize="small" />
            </Avatar>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
              {label.title}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={(e) => handleMenuClick(e, label)}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
        
        {label.description && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {label.description}
          </Typography>
        )}

        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Color:
            </Typography>
            <Chip
              size="small"
              sx={{ 
                bgcolor: label.color, 
                color: '#fff',
                minWidth: 60
              }}
              label={label.color}
            />
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Conversations:
            </Typography>
            <Chip
              size="small"
              label={label.conversation_count}
              variant="outlined"
            />
          </Box>

          {label.show_on_sidebar && (
            <Chip
              size="small"
              label="Shown in sidebar"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const renderTableView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Label</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Color</TableCell>
            <TableCell>Conversations</TableCell>
            <TableCell>Sidebar</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredLabels.map((label) => (
            <TableRow key={label.id} hover>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar
                    sx={{ 
                      bgcolor: label.color, 
                      width: 24, 
                      height: 24,
                      fontSize: '0.75rem'
                    }}
                  >
                    <LabelIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="body2" fontWeight={500}>
                    {label.title}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {label.description || '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  sx={{ 
                    bgcolor: label.color, 
                    color: '#fff',
                    minWidth: 60
                  }}
                  label={label.color}
                />
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={label.conversation_count}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={label.show_on_sidebar}
                  disabled
                  size="small"
                />
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuClick(e, label)}
                >
                  <MoreVertIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box 
        display="flex" 
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between" 
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={2}
        mb={3}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Labels
        </Typography>
        {!isMobile && (
          <PermissionGuard permission="labels:create">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Label
            </Button>
          </PermissionGuard>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Labels
              </Typography>
              <Typography variant="h4" component="div">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Sidebar Labels
              </Typography>
              <Typography variant="h4" component="div">
                {stats.sidebar}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Conversations
              </Typography>
              <Typography variant="h4" component="div">
                {stats.totalConversations}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search labels..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                select
                size="small"
                label="Filter by sidebar"
                value={filterSidebar === null ? 'all' : filterSidebar ? 'true' : 'false'}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilterSidebar(value === 'all' ? null : value === 'true');
                }}
              >
                <MenuItem value="all">All Labels</MenuItem>
                <MenuItem value="true">Sidebar Only</MenuItem>
                <MenuItem value="false">Non-Sidebar</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1}>
                <IconButton
                  onClick={() => setViewMode('cards')}
                  color={viewMode === 'cards' ? 'primary' : 'default'}
                >
                  <ViewModuleIcon />
                </IconButton>
                <IconButton
                  onClick={() => setViewMode('table')}
                  color={viewMode === 'table' ? 'primary' : 'default'}
                >
                  <ViewListIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Labels Content */}
      {filteredLabels.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <LabelIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? 'No labels found' : 'No labels yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {searchTerm 
                ? 'Try adjusting your search or filters'
                : 'Create your first label to organize conversations'
              }
            </Typography>
            {!searchTerm && (
              <PermissionGuard permission="labels:create">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Add Label
                </Button>
              </PermissionGuard>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {viewMode === 'cards' ? (
            <Grid container spacing={2}>
              {filteredLabels.map((label) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={label.id}>
                  {renderLabelCard(label)}
                </Grid>
              ))}
            </Grid>
          ) : (
            renderTableView()
          )}
        </>
      )}

      {/* Mobile FAB */}
      {isMobile && (
        <PermissionGuard permission="labels:create">
          <Fab
            color="primary"
            aria-label="add"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={() => handleOpenDialog()}
          >
            <AddIcon />
          </Fab>
        </PermissionGuard>
      )}

      {/* Label Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {editingLabel ? 'Edit Label' : 'Add New Label'}
            {isMobile && (
              <IconButton edge="end" color="inherit" onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Label Color
              </Typography>
              <ColorPicker
                color={formData.color}
                onChange={(color) => setFormData({ ...formData, color })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_on_sidebar}
                    onChange={(e) => setFormData({ ...formData, show_on_sidebar: e.target.checked })}
                  />
                }
                label="Show on sidebar"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.title.trim()}
          >
            {editingLabel ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Label</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the label "{labelToDelete?.title}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => labelToDelete && handleDelete(labelToDelete)} 
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <PermissionGuard permission="labels:edit">
          <MenuItem onClick={() => {
            if (selectedLabel) handleOpenDialog(selectedLabel);
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
        </PermissionGuard>
        <PermissionGuard permission="labels:delete">
          <MenuItem onClick={() => {
            setLabelToDelete(selectedLabel);
            setDeleteConfirmOpen(true);
            handleMenuClose();
          }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        </PermissionGuard>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LabelsPage;